import React, { useCallback, useContext } from 'react';
import { format } from 'date-fns';
import { dialogOverrides, styles, StyledInformationWrapper, StyledInformationContainer } from './PurchaseConfirmationDialog.styles';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { JoyPurchasedOrder } from './components/JoyPurchaseOrder';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { TextV2, Accordion, Flex, Box, Divider, ButtonV2, JoyKitThemeProvider } from '@withjoy/joykit';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { StyledProductPhoto } from '@apps/registry/common/components/ShoppingCart/components/ItemAtoms/ItemBase.styles';
import { addRendition } from '@shared/utils/photoRendition';
import { useEventInfo } from '@shared/utils/eventInfo';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { pxToRem } from '@withjoy/joykit/theme';
import { useGetRegistryPurchaseConfirmationDetailsQuery } from '@graphql/queries/registry.graphql.generated';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface PurchaseConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const PurchaseConfirmationDialog: React.FC<PurchaseConfirmationDialogProps> = props => {
  const { isOpen, onClose } = props;
  const purchaseContextId = usePurchaseContextIdFromLocation();
  const { data, loading } = useGetRegistryPurchaseConfirmationDetailsQuery({
    batchMode: 'fast',
    variables: purchaseContextId ? { purchaseContextId } : undefined,
    skip: !purchaseContextId,
  });
  const { eventInfo } = useEventInfo();
  const { formatCurrency } = useCurrencyFormatter();
  const { t } = useTranslation('guestRegistry');
  const { giftPurchased, haveYouBookedStay, pickedHotels, confirmationSentTo, pricesExpiring, bookYourStay, checkIn, checkOut } = t('confirmationPage');
  const { checkIn: eventStartDate, checkOut: eventEndDate } = calculateStayDates(eventInfo?.finalizedDate?.dateString);
  const analytics = useContext(AnalyticsContext);
  const [isBookingLinksEnabled, isBookingLinksEnabledLoading] = useIsFeatureEnabled({
    consentKey: 'showBookingLinksOnRegistryPurchaseConfirmation',
  });
  const shouldShowBookingLinks = isBookingLinksEnabled && !isBookingLinksEnabledLoading;

  const salesOrder = data?.registryPurchaseConfirmationDetailsByPurchaseContextId?.salesOrder;
  const items = salesOrder?.items || [];
  const firstItem = items[0];
  const brand = salesOrder?.paymentMethod?.card?.brand || '';
  const last4 = salesOrder?.paymentMethod?.card?.last4 || '';
  const isEdelivery = items.length > 0 ? items[0].isEdelivery : false;

  const getItemPhoto = useCallback(item => (item?.photos[0] !== undefined ? item.photos[0].url : undefined), []);

  const orderList = items.map(item => {
    return {
      product: { title: item.title, image: getItemPhoto(item) },
      quantity: item.quantity,
      price: formatCurrency({
        priceFloatingPointDecimalString: item.unitValue.floatingPointDecimalString,
        priceCurrencyCode: salesOrder?.subtotal.currency?.code,
        formatForm: 'short',
      }),
      totalPrice: formatCurrency({
        priceFloatingPointDecimalString: item.lineValue.floatingPointDecimalString,
        priceCurrencyCode: salesOrder?.subtotal.currency?.code,
        formatForm: 'short',
      }),
    };
  });

  const handleTelemetry = useEventCallback((name: string, action: string) => {
    analytics.track({
      category: 'guest.purchase',
      action,
      extraInfo: { name },
    });
  }, []);

  const handleBookYourStayClick = useEventCallback(() => {
    handleTelemetry('registryBookYourStayRedirect', 'ButtonInteractedClicked');
    onClose();
  });

  if (loading) {
    return null;
  }

  const orderSummary = (
    <Flex
      alignItems={'center'}
      paddingX={[0, 7]}
      justifyContent={'space-between'}
      width="100%"
    >
      <Flex>
        <Box
          height={9}
          width={9}
          alignItems="center"
        >
          <StyledProductPhoto backgroundImage={addRendition({ url: firstItem?.photos[0]?.url, renditionSize: 'medium', isProduct: true })} />
        </Box>
        <Box marginLeft={5}>
          <TextV2 typographyVariant="body1">{confirmationSentTo()}</TextV2>
          <TextV2
            typographyVariant="hed1"
            marginBottom={1}
          >
            {salesOrder?.sender?.email}
          </TextV2>
        </Box>
      </Flex>
    </Flex>
  );

  const orderDetails = (
    <StyledInformationContainer __css={styles.informationContainerStyles}>
      <StyledInformationWrapper __css={styles.informationWrapperStyles}>
        <JoyPurchasedOrder
          orderList={orderList}
          salesOrder={salesOrder}
          brand={brand}
          last4={last4}
          isEdelivery={isEdelivery}
        />
      </StyledInformationWrapper>
    </StyledInformationContainer>
  );

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider={true}
      removeBodyPadding={true}
      hasCloseButton={false}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 4, paddingX: 0 } },
      }}
      dialogOverrides={dialogOverrides}
      disableFocusLock={true}
    >
      <JoyKitThemeProvider>
        <Box
          paddingX={{ _: 6, md: 9 }}
          paddingTop={{ _: 6, md: 9 }}
        >
          <TextV2
            typographyVariant="hed4"
            textTransform={'none'}
            letterSpacing={0}
            fontWeight={600}
            color={'mono12'}
            textAlign={'center'}
          >
            {giftPurchased()}
          </TextV2>
        </Box>
        {shouldShowBookingLinks ? (
          <Accordion marginTop={6}>
            <Accordion.Item>
              <Accordion.Button
                _hover={{ backgroundColor: 'transparent' }}
                _active={{ backgroundColor: 'transparent' }}
              >
                {orderSummary}
                <Accordion.Icon style={{ marginLeft: 'auto' }} />
              </Accordion.Button>
              <Divider />
              <Accordion.Panel padding={0}>{orderDetails}</Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        ) : (
          <Box marginTop={6}>
            {orderSummary}
            <Divider />
            {orderDetails}
          </Box>
        )}
        {shouldShowBookingLinks && (
          <Box
            paddingX={{ _: 6, md: 9 }}
            paddingBottom={{ _: 6, md: 9 }}
          >
            <TextV2
              typographyVariant="hed5"
              fontWeight={700}
              textAlign={'center'}
              marginTop={6}
            >
              {haveYouBookedStay({ eventName: eventInfo?.eventDisplayName })}
            </TextV2>
            <TextV2
              typographyVariant="body2"
              textAlign={'center'}
              marginTop={6}
            >
              {pickedHotels({ eventName: eventInfo?.eventDisplayName })}
            </TextV2>
            <Flex
              marginTop={6}
              justifyContent={'center'}
              onClick={onClose}
            >
              <Box
                border="1px solid"
                borderRight={'none'}
                borderTopLeftRadius={3}
                borderBottomLeftRadius={3}
                width={pxToRem(190)}
                borderColor="mono3"
                paddingY={4}
                paddingX={6}
              >
                <TextV2
                  typographyVariant="label3"
                  marginBottom={1}
                >
                  {checkIn()}
                </TextV2>
                <TextV2 typographyVariant="body2">{format(new Date(eventStartDate.dateString), 'ccc, MMM dd')}</TextV2>
              </Box>
              <Divider orientation="vertical" />
              <Box
                border="1px solid"
                borderLeft={'none'}
                borderTopRightRadius={3}
                borderBottomRightRadius={3}
                width={pxToRem(190)}
                borderColor="mono3"
                paddingY={4}
                paddingX={6}
              >
                <TextV2
                  typographyVariant="label3"
                  marginBottom={1}
                >
                  {checkOut()}
                </TextV2>
                <TextV2 typographyVariant="body2">{format(new Date(eventEndDate.dateString), 'ccc, MMM dd')}</TextV2>
              </Box>
            </Flex>
            <TextV2
              typographyVariant={'hed2'}
              fontWeight={600}
              textAlign={'center'}
              marginTop={7}
            >
              {pricesExpiring()}
            </TextV2>
            <ButtonV2
              variant="solid"
              typographyVariant="button2"
              marginTop={5}
              fullWidth
              fontWeight={600}
              shape="rounded"
              backgroundColor={'mono14'}
              _hover={{ backgroundColor: 'mono14' }}
              onClick={handleBookYourStayClick}
            >
              {bookYourStay()}
            </ButtonV2>
          </Box>
        )}
      </JoyKitThemeProvider>
    </ResponsiveDialog>
  );
};

export default PurchaseConfirmationDialog;
